import { supabase, run } from './db';

/**
 * Área do cliente.
 *
 * Tudo passa por função, nunca por tabela. O cliente não é membro de
 * laboratório nenhum, então a RLS que já existe nega toda tabela para ele — e
 * é assim que deve continuar. Estas funções derivam o cliente do próprio login
 * e nenhuma aceita `client_id` como parâmetro: não existe o bug de passar o id
 * errado.
 */

/**
 * Duas portas para a mesma área: conta e link.
 *
 * `token` nulo significa "quem está logado"; com token, o acesso vem do link e
 * o visitante é anônimo. As funções do banco são separadas de propósito — a
 * versão por token é chamável por anônimo e a versão por conta não é, então
 * misturá-las numa função só obrigaria a abrir a de conta para o anônimo.
 */
export const meuCadastro = async (token) => {
  const linhas = token
    ? await run(supabase.rpc('cabecalho_por_token', { p_token: token }))
    : await run(supabase.rpc('meu_cadastro_de_cliente'));
  return linhas?.[0] || null;
};

export const meusServicos = (token) =>
  token
    ? run(supabase.rpc('servicos_por_token', { p_token: token }))
    : run(supabase.rpc('servicos_do_cliente'));

/**
 * O estoque do cliente: embriões no botijão e sêmen com saldo.
 *
 * Pergunta DIFERENTE da que os serviços respondem, e é por isso que tem função
 * própria. Um embrião congelado em março continua no botijão em setembro,
 * enquanto o serviço que o produziu já fechou e desceu na lista. Estoque é um
 * saldo, e saldo não se lê numa linha do tempo.
 *
 * Volta `{ embrioes, semen }` numa chamada só porque a tela mostra os dois
 * juntos: duas chamadas dariam dois estados de carregamento para uma seção.
 */
export const meuEstoque = async (token) => {
  const linhas = token
    ? await run(supabase.rpc('estoque_por_token', { p_token: token }))
    : await run(supabase.rpc('estoque_do_cliente'));
  const l = linhas?.[0];
  // Sem linha é sessão que não vale mais. Devolver as listas vazias deixa a
  // seção sumir sozinha, em vez de estourar em cima do resto da tela.
  return { embrioes: l?.embrioes || [], semen: l?.semen || [] };
};

export const meuWorkflow = (serviceId, token) =>
  token
    ? run(supabase.rpc('workflow_por_token', { p_token: token, p_service_id: serviceId }))
    : run(supabase.rpc('workflow_do_cliente', { p_service_id: serviceId }));

/**
 * O token vem da query, não do caminho.
 *
 * Um caminho como /cliente/xxx exigiria reescrita no servidor para o SPA não
 * dar 404 ao recarregar; a query funciona em qualquer hospedagem sem
 * configuração. O link fica mais feio e sempre abre.
 */
export const tokenDaUrl = () => {
  if (typeof window === 'undefined') return null;
  const t = new URLSearchParams(window.location.search).get('acesso');
  return t && t.trim() ? t.trim() : null;
};

/**
 * O link é metade da credencial; a senha é a outra.
 *
 * `situacaoDoLink` responde o que a tela precisa saber ANTES de pedir senha:
 * link vivo, link bloqueado por tentativas, ou link que não existe mais. Sem
 * isso, todo link vencido viraria "senha errada" e o cliente ficaria tentando.
 */
export const situacaoDoLink = async (token) => {
  const linhas = await run(supabase.rpc('situacao_do_link', { p_token: token }));
  return linhas?.[0] || { situacao: 'invalido' };
};

/**
 * Troca senha por sessão.
 *
 * A senha errada volta como `situacao`, não como exceção: do lado do banco isso
 * é o que permite contar tentativas, e do lado daqui evita tratar erro de rede
 * e senha errada no mesmo catch — são coisas diferentes para quem está olhando.
 */
export const abrirComSenha = async (token, senha) => {
  const linhas = await run(supabase.rpc('abrir_com_senha', { p_token: token, p_senha: senha }));
  return linhas?.[0] || { situacao: 'invalido' };
};

/**
 * A sessão fica guardada por link, não por aparelho.
 *
 * Um celular de escritório pode acompanhar mais de um cliente; uma chave só
 * faria o segundo link derrubar o primeiro. Tudo em try/catch porque em aba
 * anônima o localStorage existe mas lança na hora de escrever.
 */
const chaveDaSessao = (token) => `fiv_acesso_${String(token).slice(0, 12)}`;

export const sessaoGuardada = (token) => {
  try { return window.localStorage.getItem(chaveDaSessao(token)) || null; } catch { return null; }
};

export const guardarSessao = (token, sessao) => {
  try { window.localStorage.setItem(chaveDaSessao(token), sessao); } catch { /* segue sem lembrar */ }
};

export const esquecerSessao = (token) => {
  try { window.localStorage.removeItem(chaveDaSessao(token)); } catch { /* nada a fazer */ }
};

/** Gera (e substitui) o link de um cliente. Devolve o token cru. */
export const gerarLink = (clientId, senha, dias = 180) =>
  run(supabase.rpc('gerar_link_do_cliente', { p_client_id: clientId, p_senha: senha, p_dias: dias }));

export const linkDoCliente = (clientId) =>
  run(supabase
    .from('client_access_links')
    .select('*')
    .eq('client_id', clientId)
    .is('revogado_em', null)
    .maybeSingle());

export const revogarLink = (id) =>
  run(supabase.from('client_access_links').update({ revogado_em: new Date().toISOString() }).eq('id', id));

/**
 * Troca a senha mantendo o endereço.
 *
 * O banco só guarda o hash, então uma senha esquecida era irrecuperável e o
 * único caminho era gerar outro link — que muda a URL e obriga a reenviar tudo.
 * A troca derruba as sessões abertas: quem troca senha está tirando alguém de
 * dentro.
 */
export const redefinirSenha = (clientId, senha) =>
  run(supabase.rpc('redefinir_senha_do_link', { p_client_id: clientId, p_senha: senha }));

/** O endereço que vai para o WhatsApp. */
export const urlDoLink = (token) =>
  `${window.location.origin}${window.location.pathname}?acesso=${token}`;

/**
 * A mensagem pronta para mandar.
 *
 * Link e senha juntos numa mensagem só: é o que se pede na prática, e separar
 * em duas só faz sentido enquanto alguém realmente usa dois canais — no mesmo
 * fio de WhatsApp, as duas mensagens vão para o mesmo lugar de qualquer jeito.
 * Sem senha em mãos (link antigo, hash já não se lê), manda só o link.
 */
export const mensagemDoLink = ({ cliente, url, senha }) => {
  const abertura = cliente ? `Olá, ${cliente}!` : 'Olá!';
  const linhas = [
    `${abertura} Acompanhe seus serviços por aqui:`,
    url,
  ];
  if (senha) linhas.push('', `Senha de acesso: ${senha}`);
  return linhas.join('\n');
};

/**
 * Telefone brasileiro para o formato que o wa.me aceita.
 *
 * O cadastro tem de tudo: "(34) 99999-8888", "34999998888", já com 55 na
 * frente. Devolve null quando não dá para confiar no que está lá — aí o
 * WhatsApp abre sem destinatário e a pessoa escolhe, que é melhor do que
 * mandar a senha de um cliente para o número errado.
 */
export const telefoneWhatsapp = (telefone) => {
  const so = String(telefone || '').replace(/\D/g, '');
  if (!so) return null;
  if (so.startsWith('55') && (so.length === 12 || so.length === 13)) return so;
  if (so.length === 10 || so.length === 11) return `55${so}`;
  return null;
};

export const linkDoWhatsapp = (telefone, mensagem) => {
  const numero = telefoneWhatsapp(telefone);
  return `https://wa.me/${numero || ''}?text=${encodeURIComponent(mensagem)}`;
};

/**
 * O mesmo número, mas para só abrir a conversa.
 *
 * `linkDoWhatsapp` serve ao envio do link de acesso, que já vai com texto
 * pronto. O clique na carteira é outro assunto: quem vai ligar só quer o fio
 * aberto com quem atende, e chegar lá com uma mensagem escrita por nós seria
 * pôr palavra na boca de quem cobra.
 *
 * Devolve null quando o cadastro não dá um número confiável — aí o telefone
 * continua sendo texto na tela. Um link que abre o WhatsApp sem destinatário
 * promete conversa e entrega uma lista de contatos.
 */
export const conversaDoWhatsapp = (telefone) => {
  const numero = telefoneWhatsapp(telefone);
  return numero ? `https://wa.me/${numero}` : null;
};

/**
 * As etapas de um serviço, na ordem do fluxo e só as contratadas.
 *
 * Mostrar etapa não contratada como "pendente" faria o cliente esperar por algo
 * que nunca vai acontecer — e ligar para perguntar.
 */
export const etapasDoServico = (s) => {
  const contratadas = s.services_offered || [];
  const etapas = [
    { id: 'opu',  rotulo: 'OPU',  requer: 'opu',  feito: s.opu_done,  data: s.opu_date,  previsto: s.opu_scheduled,  qtd: s.oocitos,     unidade: 'oócitos' },
    { id: 'fiv',  rotulo: 'FIV',  requer: 'fiv',  feito: s.fiv_done,  data: s.fiv_date,  previsto: s.fiv_scheduled,  qtd: s.embrioes,    unidade: 'embriões' },
    { id: 'te',   rotulo: 'TE',   requer: 'te',   feito: s.te_done,   data: s.te_date,   previsto: s.te_scheduled,   qtd: s.transferidos, unidade: 'transferidos' },
    { id: 'dg30', rotulo: 'DG 30 dias', requer: 'pregnancy_check', feito: s.dg30_done, data: s.dg30_date, previsto: s.dg30_scheduled, qtd: null },
    { id: 'dg60', rotulo: 'DG 60 dias', requer: 'pregnancy_check', feito: s.dg60_done, data: s.dg60_date, previsto: s.dg60_scheduled, qtd: s.prenhes, unidade: 'prenhes' },
  ];
  return etapas.filter(e => contratadas.includes(e.requer));
};

/**
 * O funil de tudo que o cliente já tem pronto.
 *
 * Só entra etapa concluída — e a etapa que ninguém concluiu ainda nem vira
 * degrau. Um funil terminando em "Prenhes 0" porque nenhum DG aconteceu leria
 * como fracasso, quando na verdade é "ainda não chegou lá".
 */
export const resumoGeral = (servicos = []) => {
  const degraus = [
    { etapa: 'Oócitos',      feito: 'opu_done',  campo: 'oocitos' },
    { etapa: 'Embriões',     feito: 'fiv_done',  campo: 'embrioes' },
    { etapa: 'Transferidos', feito: 'te_done',   campo: 'transferidos' },
    { etapa: 'Prenhes',      feito: 'dg60_done', campo: 'prenhes' },
  ];

  const vivos = degraus
    .filter(d => servicos.some(s => s[d.feito]))
    .map(d => ({
      etapa: d.etapa,
      valor: servicos.reduce((t, s) => t + (s[d.feito] ? Number(s[d.campo]) || 0 : 0), 0),
    }));

  // A conversão é sempre sobre o degrau ANTERIOR, não sobre o topo: é onde a
  // perda realmente acontece.
  return vivos.map((d, i) => {
    const anterior = vivos[i - 1];
    return {
      ...d,
      taxa: !anterior || anterior.valor === 0 ? null : Math.round((d.valor / anterior.valor) * 100),
    };
  });
};

/**
 * O que cada etapa concluída mostra quando o cliente a abre.
 *
 * O `detalhe` já chega filtrado do banco — etapa que não fechou vem vazia. Aqui
 * é só distribuição: cada etapa recebe o que ela produziu, e o resumo de uma
 * linha que aparece fechada, para não ter que abrir para saber se vale a pena.
 */
export const conteudoDaEtapa = (etapaId, detalhe) => {
  const { opu = [], fiv = [], embrioes = [], te = [], prenhez = [] } = detalhe || {};
  const total = (lista, campo) => lista.reduce((t, x) => t + (Number(x[campo]) || 0), 0);

  if (etapaId === 'opu') {
    return { tipo: 'opu', linhas: opu,
             resumo: opu.length ? `${total(opu, 'aspirados')} oócitos de ${opu.length} doadoras` : null };
  }
  if (etapaId === 'fiv') {
    // Doadoras contadas nos EMBRIÕES, não nas linhas da FIV: quem herda a FIV
    // de outro serviço recebe a FIV inteira, mas só os embriões dele — o
    // resumo dizia "1 embriões de 14 doadoras" na 2026-0015.
    const doadoras = new Set(embrioes.map(e => e.doadora)).size;
    return { tipo: 'fiv', linhas: fiv, embrioes,
             resumo: embrioes.length ? `${embrioes.length} embriões de ${doadoras} doadoras` : null };
  }
  if (etapaId === 'te') {
    // "Transferido" é a APROVADA; as demais foram avaliadas e não seguiram.
    const feitas = te.filter(t => (t.status || '').toUpperCase() === 'APROVADA');
    return { tipo: 'te', linhas: te, feitas,
             resumo: te.length ? `${feitas.length} de ${te.length} embriões transferidos` : null };
  }
  if (etapaId === 'dg30' || etapaId === 'dg60') {
    const alvo = etapaId.toUpperCase();
    const linhas = prenhez.filter(p => (p.etapa || '').toUpperCase() === alvo);
    const prenhas = linhas.filter(p => (p.resultado || '').toUpperCase().startsWith('PRENH'));
    return { tipo: 'prenhez', linhas, prenhas,
             resumo: linhas.length ? `${prenhas.length} prenhes de ${linhas.length} receptoras` : null };
  }
  return { tipo: 'vazio', linhas: [] };
};

/**
 * Em que ponto o serviço está: a última etapa concluída.
 *
 * "Concluído" só quando TODAS as contratadas terminaram — parar na última feita
 * daria a impressão de fim antes da hora.
 */
export const andamento = (s) => {
  const etapas = etapasDoServico(s);
  if (etapas.length === 0) return { rotulo: 'Sem etapas', feitas: 0, total: 0 };

  const feitas = etapas.filter(e => e.feito).length;
  if (feitas === etapas.length) return { rotulo: 'Concluído', feitas, total: etapas.length };

  const proxima = etapas.find(e => !e.feito);
  return { rotulo: `Aguardando ${proxima.rotulo}`, feitas, total: etapas.length };
};

/**
 * O estoque em uma linha, para o cabeçalho da seção.
 *
 * Conta EMBRIÃO e soma DOSE — são unidades diferentes, e somá-las num número
 * só daria "4 itens" para 3 embriões e 1 dose, que não é coisa nenhuma.
 *
 * `vazio` é o que faz a seção sumir. Cliente sem nada guardado não precisa de
 * um cartão dizendo "0" — isso é ocupar a tela para não informar nada.
 */
export const resumoDoEstoque = ({ embrioes = [], semen = [] } = {}) => {
  const doses = (semen || []).reduce((t, p) => t + (Number(p?.doses) || 0), 0);
  return {
    embrioes: (embrioes || []).length,
    // Arredonda na casa da dose: meia dose somada em ponto flutuante rende
    // 2.9999999, e o cliente não pode ver isso.
    doses: Math.round(doses * 100) / 100,
    partidas: (semen || []).length,
    vazio: (embrioes || []).length === 0 && (semen || []).length === 0,
  };
};

/**
 * Embriões agrupados por doadora × touro.
 *
 * O congelado NÃO TEM NÚMERO desde que a numeração passou a ser só do que vai
 * a campo — a palheta no botijão se acha pelo botijão e pela caneca, que é
 * mapa do laboratório e não vai para esta tela. Então listar um por linha
 * daria oito linhas idênticas sem nada que as distinga.
 *
 * O cruzamento é o que o cliente reconhece e o que ele pede pelo telefone:
 * "manda dois da RENASCENÇA com GALATES".
 */
export const embrioesPorCruzamento = (embrioes = []) => {
  const mapa = new Map();
  for (const e of embrioes || []) {
    const doadora = e?.doadora || '—';
    const touro = e?.touro || '—';
    const k = `${doadora} ${touro}`;
    if (!mapa.has(k)) mapa.set(k, { doadora, touro, quantidade: 0, estagios: new Set() });
    const g = mapa.get(k);
    g.quantidade += 1;
    if (e?.estagio) g.estagios.add(e.estagio);
  }
  return [...mapa.values()]
    .map(g => ({ ...g, estagios: [...g.estagios].sort() }))
    .sort((a, b) => b.quantidade - a.quantidade
      || a.doadora.localeCompare(b.doadora)
      || a.touro.localeCompare(b.touro));
};

/**
 * Os períodos que o portal oferece.
 *
 * Presets em vez de dois campos de data: a tela do cliente é de celular, e
 * escolher duas datas num teclado de telefone é atrito para uma pergunta que
 * quase sempre é "este ano" ou "tudo".
 */
export const PERIODOS = [
  { id: 'tudo', rotulo: 'Tudo',          meses: null },
  { id: '12m',  rotulo: 'Últimos 12 meses', meses: 12 },
  { id: '6m',   rotulo: 'Últimos 6 meses',  meses: 6 },
  { id: '3m',   rotulo: 'Últimos 3 meses',  meses: 3 },
];

/**
 * Recorta os serviços pelo período escolhido.
 *
 * Olha `service_date` — a data do contrato —, e não a de cada etapa: é a mesma
 * régua que o relatório do laboratório usa, e duas réguas diferentes para a
 * mesma palavra "período" fariam os dois números divergirem sem explicação.
 *
 * `hoje` entra por parâmetro para o teste não depender do calendário.
 */
export const filtrarPorPeriodo = (servicos = [], periodoId = 'tudo', hoje = new Date()) => {
  const p = PERIODOS.find(x => x.id === periodoId);
  if (!p || p.meses === null) return servicos || [];

  const limite = new Date(hoje);
  limite.setMonth(limite.getMonth() - p.meses);
  const corte = limite.toISOString().slice(0, 10);

  // Serviço sem data fica DE FORA de um recorte por data. Mantê-lo faria o
  // total do período incluir algo que ninguém sabe se pertence a ele.
  return (servicos || []).filter(s => {
    const d = String(s?.service_date || '').slice(0, 10);
    return d !== '' && d >= corte;
  });
};
